import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrderMobileCardShell from "./OrderMobileCardShell";
import OrdersSubTabs from "./OrdersSubTabs";
import AuthService from "../../../api/services/AuthService";
import { spotActivityDefaultLocalRange } from "./orderFilterHelpers";
import { MARGIN_SUB_TABS, MARGIN_ACCOUNT_TYPE_OPTIONS, buildMarginPairOptions } from "./marginOrderFilterConstants";
import OrderTablePagination, { useOrderTablePagination } from "./OrderTablePagination";

const ORDER_DESKTOP_COL_COUNT = 7;

const DIRECTION_OPTIONS = [
  { value: "all", label: "All" },
  { value: "TO_MARGIN", label: "To Margin" },
  { value: "FROM_MARGIN", label: "From Margin" },
];

const DIRECTION_LABELS = {
  TO_MARGIN: "To Isolated Margin",
  FROM_MARGIN: "From Isolated Margin",
  TO_CROSS: "To Cross Margin",
  FROM_CROSS: "From Cross Margin",
};

const WALLET_LABELS = {
  spot: "Spot Wallet",
  main: "Main Wallet",
  margin: "Isolated Margin",
  cross_margin: "Cross Margin",
};

function fmtDirection(v) { return DIRECTION_LABELS[v] ?? v ?? "—"; }
function fmtWallet(v) { return WALLET_LABELS[String(v).toLowerCase()] ?? v ?? "—"; }

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function formatPair(raw) {
  if (!raw) return "—";
  if (raw.includes("/")) return raw;
  if (raw.length >= 6) {
    const known = ["USDT", "BTC", "ETH", "BNB", "USDC"];
    for (const q of known) {
      if (raw.endsWith(q)) return `${raw.slice(0, raw.length - q.length)}/${q}`;
    }
  }
  return raw;
}

function toIsoFrom(dateStr) {
  return dateStr ? `${dateStr}T00:00:00.000Z` : undefined;
}
function toIsoTo(dateStr) {
  return dateStr ? `${dateStr}T23:59:59.999Z` : undefined;
}

function DatePickerCell({ value, onChange, ariaLabel, inputRef, placeholder = "From" }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">{value || placeholder}</span>
      <input ref={inputRef} type="date" className="so-dateCell__native" value={value} onChange={(e) => onChange(e.target.value)} aria-label={ariaLabel} />
    </div>
  );
}

const MarginTransferHistory = () => {
  const initRange = spotActivityDefaultLocalRange();

  const [accountType, setAccountType] = useState("isolated");
  const [direction, setDirection] = useState("all");
  const [contract, setContract] = useState("all");
  const [dateFrom, setDateFrom] = useState(initRange.from);
  const [dateTo, setDateTo] = useState(initRange.to);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pairOptions, setPairOptions] = useState([{ value: "all", label: "All" }]);
  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(rows);
  const [rawAccounts, setRawAccounts] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);

  const dateFromRefDesk = useRef(null);
  const dateToRefDesk = useRef(null);
  const dateFromRefMob = useRef(null);
  const dateToRefMob = useRef(null);

  useEffect(() => {
    AuthService.marginAccounts()
      .then((res) => {
        if (res?.success) {
          setRawAccounts(res.data || []);
          setPairOptions(buildMarginPairOptions(res.data || []));
        }
      })
      .catch(() => { });
  }, []);

  const resolvedPairId = contract === "all"
    ? undefined
    : rawAccounts.find((a) => `${a.base_asset}_${a.quote_asset}`.toLowerCase() === contract)?.pair_id;

  const loadHistory = useCallback(async (dir, pairId, from, to) => {
    setLoading(true);
    try {
      const res = accountType === "cross"
        ? await AuthService.crossTransferHistory({ direction: dir !== "all" ? dir : undefined, page: 1, limit: 100 })
        : await AuthService.marginTransferHistory({ pairId, direction: dir !== "all" ? dir : undefined, from: toIsoFrom(from), to: toIsoTo(to) });
      if (res?.success) {
        // Cross response: { data: { transfers: [...] } }
        // Isolated response: res.data is the array directly
        const raw = accountType === "cross"
          ? (res.data?.transfers ?? [])
          : (res.data || []);
        setRows(
          raw.map((r) => ({
            id: r.transfer_id ?? r.id ?? r.time,
            time: formatDate(r.created_at ?? r.time),
            pair: accountType === "cross" ? (r.asset ?? "—") : formatPair(r.contract),
            coin: r.asset ?? r.coin ?? "—",
            amount: r.amount ?? "—",
            direction: fmtDirection(r.direction ?? r.type),
            fromWallet: fmtWallet(r.from_wallet ?? r.from),
            toWallet: fmtWallet(r.to_wallet ?? r.to),
          }))
        );
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [accountType]);

  useEffect(() => {
    loadHistory(direction, resolvedPairId, dateFrom, dateTo);
  }, [accountType, direction, resolvedPairId, dateFrom, dateTo, loadHistory]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      if (!(e.target instanceof Element)) return;
      if (!e.target.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpenDropdown(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const resetFilters = () => {
    const { from, to } = spotActivityDefaultLocalRange();
    setDirection("all"); setContract("all"); setDateFrom(from); setDateTo(to);
  };

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (el && typeof el.showPicker === "function") { try { el.showPicker(); return; } catch { /* */ } }
    el?.click();
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const fromRef = keyPrefix === "mob" ? dateFromRefMob : dateFromRefDesk;
    const toRef = keyPrefix === "mob" ? dateToRefMob : dateToRefDesk;
    return (
      <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter margin transfer history">
        <div className="so-toolbar__left fullwidthdesktop">
          <CustomDropdown id={`${pref}mtf-account`} label="Account" value={accountType} options={MARGIN_ACCOUNT_TYPE_OPTIONS}
            onChange={(v) => { setAccountType(v); setContract("all"); }} menuKey={`${pref}mtf-account`} openKey={openDropdown} onOpenToggle={setOpenDropdown} />
          <CustomDropdown id={`${pref}mtf-dir`} label="Direction" value={direction} options={DIRECTION_OPTIONS} onChange={setDirection} menuKey={`${pref}mtf-dir`} openKey={openDropdown} onOpenToggle={setOpenDropdown} />
          {accountType === "isolated" && (
            <CustomDropdown id={`${pref}mtf-contract`} label="Contract" value={contract} options={pairOptions} onChange={setContract} menuKey={`${pref}mtf-contract`} openKey={openDropdown} onOpenToggle={setOpenDropdown} />
          )}
          <div className="so-dateBlock">
            <span className="so-dateBlock__label">Date</span>
            <div className="so-dateRow">
              <DatePickerCell value={dateFrom} onChange={setDateFrom} placeholder="From" ariaLabel="Start date" inputRef={fromRef} />
              <span className="so-dateArrow" aria-hidden="true">→</span>
              <DatePickerCell value={dateTo} onChange={setDateTo} placeholder="To" ariaLabel="End date" inputRef={toRef} />
              <button type="button" className="so-dateCalBtn" aria-label="Open calendar" onClick={() => openDatePicker(toRef)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M3 10h18" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          {loading
            ? <div className="so-emptyState__text">Loading…</div>
            : <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="mtf-title">
            <h1 id="mtf-title" className="so-page__title">Transfer History</h1>
            <OrdersSubTabs tabs={MARGIN_SUB_TABS} />
            {renderToolbar(false, "desk")}
            <div className="so-tableWrap">
              <table className="so-table so-table--rowLines">
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Contract</th>
                    <th scope="col">Coin</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Direction</th>
                    <th scope="col">From</th>
                    <th scope="col">To</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? renderEmptyBody() : pagedRows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.time}</td>
                      <td>{r.pair}</td>
                      <td>{r.coin}</td>
                      <td className="so-num">{r.amount}</td>
                      <td style={{ color: r.direction.startsWith("To") ? "#01bc8d" : "#e45561" }}>
                        {r.direction}
                      </td>
                      <td style={{ fontSize: 12, color: "#8E949E" }}>{r.fromWallet}</td>
                      <td style={{ fontSize: 12, color: "#8E949E" }}>{r.toWallet}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="Transfer history pages"
            />
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Transfer History</h2>
          <OrdersSubTabs tabs={MARGIN_SUB_TABS} />
          <div className="so-mobile__toolbar">
            <SoMobileFilters modalId="mob-mtf-filters" toolbar={renderToolbar(true, "mob")} onReset={resetFilters} />
          </div>
          {rows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              {loading
                ? <div className="so-emptyState__text">Loading…</div>
                : <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />}
            </div>
          ) : pagedRows.map((r) => (
            <OrderMobileCardShell key={`m-${r.id}`} desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Transfer details">
              <div className="so-mobileCard__row"><span>Time</span><span>{r.time}</span></div>
              <div className="so-mobileCard__row"><span>Contract</span><span>{r.pair}</span></div>
              <div className="so-mobileCard__row"><span>Coin</span><span>{r.coin}</span></div>
              <div className="so-mobileCard__row"><span>Amount</span><span className="so-num">{r.amount}</span></div>
              <div className="so-mobileCard__row">
                <span>Direction</span>
                <span style={{ color: r.direction.startsWith("To") ? "#01bc8d" : "#e45561" }}>{r.direction}</span>
              </div>
              <div className="so-mobileCard__row"><span>From</span><span style={{ color: "#8E949E" }}>{r.fromWallet}</span></div>
              <div className="so-mobileCard__row"><span>To</span><span style={{ color: "#8E949E" }}>{r.toWallet}</span></div>
            </OrderMobileCardShell>
          ))}

          <OrderTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label="Transfer history pages"
          />
        </div>
      </div>
    </div>
  );
};

export default MarginTransferHistory;
